import { Box, BoxProps, Collapse, Divider, SvgIconProps, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useMemo, useState } from 'react';

export type SectionContainerProps = {
  title: string;
  children: React.ReactNode;
  id?: string;
  forceMobile?: boolean;
};

export const SectionContainer: React.VFC<SectionContainerProps> = props => {
  const { classes, cx } = useSectionContainerStyles();

  return (
    <Box
      component="section"
      className={classes.section}
      id={props.id}
    >
      <Box className={cx(classes.sectionTitle, { [classes.sectionTitleDesktop]: !props.forceMobile })}>
        <Typography variant="h4" component="h2">{props.title}</Typography>
      </Box>
      <Box className={cx(classes.sectionCard, { [classes.sectionCardDesktop]: !props.forceMobile })}>
        {props.children}
      </Box>
    </Box>
  );
};

export type SubSectionContainerProps = {
  title: string;
  children: React.ReactNode;
  icon: React.ComponentType<SvgIconProps>;
  large?: boolean;
  contentInline?: boolean;
  forceMobile?: boolean;
} & Omit<BoxProps, 'title' | 'children'> & SubSectionContainerWrapperProps;

export const SubSectionContainer: React.VFC<SubSectionContainerProps> = ({ in: tranistionIn, title, children, icon, large, contentInline, forceMobile, id, highlight, ...boxProps }) => {
  const { classes, cx } = useSectionContainerStyles();

  const iconJsx = useMemo(() => {
    if (icon) {
      const Icon = icon;

      return (
        <Box
          className={cx(classes.subSectionIconContainer, {
            [classes.subSectionIconContainerDesktop]: !forceMobile,
            [classes.subSectionIconContainerLarge]: large,
            [classes.subSectionIconContainerLargeDesktop]: !forceMobile && large
          })}
        >
          <Icon color="secondary" />
        </Box>
      );
    }

    return null;
  }, [ classes, icon, large, forceMobile ]);

  const content = useMemo(() => {

    if (contentInline) {
      return (
        <Box display="flex" alignItems="center" gap="0.5rem">
          {iconJsx}
          <Box mt={forceMobile ? 0 : { sm: 0.5 }}>
            <Typography
              component="span"
              className={cx(classes.subSectionTitle, large && classes.subSectionTitleLarge)}
            >
              {title}&nbsp;
            </Typography>
            {children}
          </Box>
        </Box>
      );
    }

    return (
      <Box>
        {(title || iconJsx) && (
          <Box display="flex" alignItems="center" gap="0.5rem" mb={1}>
            {iconJsx}
            <Typography
              component="h3"
              className={cx(classes.subSectionTitle, large && classes.subSectionTitleLarge)}
            >
              {title}
            </Typography>
          </Box>
        )}
        <Box className={cx(classes.subSectionContent, {
          [classes.subSectionContentDesktop]: !forceMobile,
          [classes.subSectionContentLarge]: large,
          [classes.subSectionContentLargeDesktop]: !forceMobile && large
        })}
        >
          {children}
        </Box>
      </Box>
    );
  }, [ children, classes, contentInline, forceMobile, iconJsx, large, title ]);

  return (
    <SubSectionContainerWrapper id={id} highlight={highlight} in={tranistionIn} hidden={boxProps.hidden}>
      <Box
        className={cx(classes.subSection, {
          [classes.subSectionDesktop]: !forceMobile,
          [classes.subSectionLarge]: large,
          [classes.subSectionLargeDesktop]: !forceMobile && large
        })}
        {...boxProps}
      >
        {content}
      </Box>
    </SubSectionContainerWrapper>
  );
};

export type SubSectionContainerWrapperProps = {
  id: string;
  highlight?: boolean;
  in?: boolean;
  hidden?: boolean;
};

export const SubSectionContainerWrapper: React.FC<SubSectionContainerWrapperProps> = ({ in: transitionIn, children, highlight, id, hidden }) => {
  const [ transitionInClass, setTransitionInClass ] = useState(transitionIn ?? true);
  const { classes, cx } = useSectionContainerStyles();

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (transitionIn && !transitionInClass) {
      timeout = setTimeout(() => setTransitionInClass(true), 500);
    }

    return () => clearTimeout(timeout);
  }, [ transitionIn ]);

  if (hidden) {
    return null;
  }

  return (
    (
      <Box
        component="section"
        id={id}
        className={cx(classes.subSectionContainerWrapper, {
          [classes.subSectionContainerWrapperHightlight]: highlight
        })}
      >
        <Collapse in={transitionInClass} timeout={900}>
          {children}
          <Divider />
        </Collapse>
      </Box>
    )
  );
};

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useSectionContainerStyles = makeStyles({ name: 'SelectedProvider' })((theme) => ({
  section: {

  },

  sectionCard: {
    background: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    // borderBottom: `1px solid ${theme.palette.divider}`,
  },
  sectionCardDesktop: {
    [theme.breakpoints.up('sm')]: {
      border: 'none',
      boxShadow: theme.shadows[4],
    }
  },

  sectionTitle: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(0, 2),
  },
  sectionTitleDesktop: {
    [theme.breakpoints.up('sm')]: {
      marginBottom: theme.spacing(2),
      padding: theme.spacing(0),
    }
  },

  subSectionContainerWrapper: {
    background: 'transparent',
    transition: 'background 0.9s ease-in 0s',
  },
  subSectionContainerWrapperHightlight: {
    background: theme.palette.secondary.background,
  },

  subSection: {
    padding: theme.spacing(2),
  },
  subSectionDesktop: {
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(3),
    }
  },

  subSectionLarge: {
    padding: theme.spacing(3, 2),
  },
  subSectionLargeDesktop: {
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(3),
    }
  },

  subSectionIconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: theme.palette.secondary.background,

    width: '24px',
    height: '24px',
    minWidth: '24px',
    '& svg': {
      fontSize: '18px',
    },
  },
  subSectionIconContainerDesktop: {
    [theme.breakpoints.up('sm')]: {
      width: '32px',
      height: '32px',
      minWidth: '32px',
    }
  },

  subSectionIconContainerLarge: {
    width: '32px',
    height: '32px',
    minWidth: '32px',
    '& svg': {
      fontSize: '18px'
    },
  },
  subSectionIconContainerLargeDesktop: {
    [theme.breakpoints.up('sm')]: {
      width: '48px',
      height: '48px',
      minWidth: '48px',
      '& svg': {
        fontSize: '24px'
      }
    },
  },

  subSectionContent: {
    marginLeft: '32px',
  },
  subSectionContentDesktop: {
    [theme.breakpoints.up('sm')]: {
      marginLeft: '40px',
    },
  },

  subSectionContentLarge: {
    marginLeft: '40px',
  },
  subSectionContentLargeDesktop: {
    [theme.breakpoints.up('sm')]: {
      marginLeft: '56px',
    },
  },

  subSectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  subSectionTitleLarge: {
    fontSize: '18px',
    fontWeight: 700,
  }
}));
